/**
 * Compile Gate
 * 
 * Rules engine output with fail reasons, severity, approval requirements.
 * Redesigned as triage-first, dense, fast, and action-driven.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Download, Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { API_BASE_URL } from '@/config';
import { getAuthHeaders } from '@/api/workosAuth';
import MainLayout from '@/components/layout/MainLayout';
import { PageLoading } from '@/components/ui/loading';
import { EmptyState } from '@/components/ui/empty';
import { CompileGateSummary } from '@/components/project-state/CompileGateSummary';
import { CompileGateFilters } from '@/components/project-state/CompileGateFilters';
import { CompileGateTable } from '@/components/project-state/CompileGateTable';
import { TriageQueue } from '@/components/project-state/TriageQueue';
import { useCompileGateViewModel, Violation } from '@/hooks/useCompileGateViewModel';
import { exportToCSV } from '@/utils/exportUtils';

interface CompileGateData {
  overall_status: string;
  violations: Violation[];
  summary: any;
  message?: string;
}

export const CompileGate: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const [compileData, setCompileData] = useState<CompileGateData | null>(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [showTriageQueue] = useState(true);

  useEffect(() => {
    if (projectId) {
      // Auto-run compile gate when page loads
      runCompileGate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const runCompileGate = async () => {
    try {
      setRunning(true);
      setLoading(true);
      const headers = await getAuthHeaders();
      const compileResponse = await fetch(`${API_BASE_URL}/v1/policies/compile/${projectId}`, {
        method: 'POST',
        credentials: 'include',
        headers,
      });
      if (!compileResponse.ok) {
        const errorData = await compileResponse.json().catch(() => ({}));
        if (compileResponse.status === 404) {
          setCompileData({
            overall_status: 'pending',
            violations: [],
            summary: { total_violations: 0 },
            message: errorData.message || 'No snapshot found. Upload a XER file to create a snapshot.'
          });
          return;
        }
        throw new Error(errorData.message || 'Failed to run compile gate');
      }
      const compileData = await compileResponse.json();
      setCompileData(compileData);
    } catch (err) {
      console.error('Failed to run compile gate:', err);
      // Fallback to just fetching violations if compile fails
      try {
        const headers = await getAuthHeaders();
        const response = await fetch(`${API_BASE_URL}/v1/policies/violations/${projectId}`, {
          credentials: 'include',
          headers,
        });
        if (response.ok) {
          const data = await response.json();
          setCompileData(data);
        } else {
          setCompileData({
            overall_status: 'error',
            violations: [],
            summary: { total_violations: 0 },
            message: err instanceof Error ? err.message : 'Failed to load violations'
          });
        }
      } catch {
        setCompileData({
          overall_status: 'error',
          violations: [],
          summary: { total_violations: 0 },
          message: err instanceof Error ? err.message : 'Failed to load violations'
        });
      }
    } finally {
      setLoading(false);
      setRunning(false);
    }
  };

  // Use view model hook
  const violations = compileData?.violations || [];
  const hasWBS = violations.some(v => Boolean(v.wbs_path || v.wbs_code));
  
  const viewModel = useCompileGateViewModel(violations);

  // Handle export
  const handleExport = useCallback((type: 'all' | 'filtered' | 'selected' | 'group', groupKey?: string) => {
    const exportData = viewModel.getExportData(type, groupKey);
    if (exportData.length === 0) {
      return;
    }

    const timestamp = new Date().toISOString().split('T')[0];
    let filename = `compile-gate-violations-${timestamp}.csv`;
    
    if (type === 'selected') {
      filename = `compile-gate-selected-${timestamp}.csv`;
    } else if (type === 'group' && groupKey) {
      filename = `compile-gate-${groupKey}-${timestamp}.csv`;
    }

    exportToCSV(exportData, filename);
  }, [viewModel]);

  // Handle violation click (for triage queue navigation)
  const handleViolationClick = useCallback((violationId: string) => {
    // Scroll to violation in table
    const element = document.getElementById(`violation-${violationId}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, []);

  // Handle sort
  const handleSort = useCallback((column: string) => {
    const currentColumn = viewModel.viewState.sortColumn;
    const currentDirection = viewModel.viewState.sortDirection;
    
    const newDirection = currentColumn === column && currentDirection === 'desc' ? 'asc' : 'desc';
    
    viewModel.setViewState({
      ...viewModel.viewState,
      sortColumn: column,
      sortDirection: newDirection
    });
  }, [viewModel]);

  // Handle severity toggle
  const handleSeverityToggle = useCallback((severity: string) => {
    const current = viewModel.viewState.selectedSeverities;
    const newSeverities = current.includes(severity)
      ? current.filter(s => s !== severity)
      : [...current, severity];
    
    viewModel.setViewState({
      ...viewModel.viewState,
      selectedSeverities: newSeverities
    });
  }, [viewModel]);

  // Handle rule toggle
  const handleRuleToggle = useCallback((ruleId: string) => {
    const current = viewModel.viewState.selectedRules;
    const newRules = current.includes(ruleId)
      ? current.filter(r => r !== ruleId)
      : [...current, ruleId];
    
    viewModel.setViewState({
      ...viewModel.viewState,
      selectedRules: newRules
    });
  }, [viewModel]);

  // Bulk mark reviewed
  const handleBulkMarkReviewed = useCallback(() => {
    if (viewModel.violationState.selected.size > 0) {
      viewModel.bulkMarkReviewed(Array.from(viewModel.violationState.selected));
    }
  }, [viewModel]);

  if (loading) {
    return (
      <MainLayout title="Compile Gate">
        <PageLoading message="Loading compile gate..." />
      </MainLayout>
    );
  }

  if (compileData?.message && compileData.violations.length === 0) {
    return (
      <MainLayout title="Compile Gate">
        <EmptyState
          title="No Compile Data Available"
          description={compileData.message}
        />
      </MainLayout>
    );
  }

  const violationRows = viewModel.flattenedRows.filter(r => r.type === 'violation');
  const allSelected = violationRows.length > 0 &&
    Array.from(viewModel.violationState.selected).length === violationRows.length;

  // Get violations for table
  // For now, flatten grouped violations - group headers can be added later
  const tableViolations = viewModel.viewState.groupBy === 'none'
    ? viewModel.sortedViolations
    : Object.values(viewModel.groupedViolations).flat();

  return (
    <MainLayout title="Compile Gate">
      <div className="p-6 w-full" style={{ width: '100%', maxWidth: 'none', boxSizing: 'border-box' }}>
        <div className="w-full" style={{ width: '100%', maxWidth: 'none', boxSizing: 'border-box' }}>
          {/* Header Actions */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <Button onClick={runCompileGate} disabled={running} variant="outline">
                <Play className="h-4 w-4 mr-2" />
                {running ? 'Refreshing...' : 'Refresh'}
              </Button>
              {compileData && compileData.violations.length > 0 && (
                <>
                  <Button onClick={() => handleExport('filtered')} variant="outline" size="sm">
                    <Download className="h-4 w-4 mr-2" />
                    Export CSV
                  </Button>
                  {viewModel.violationState.selected.size > 0 && (
                    <>
                      <Button onClick={handleBulkMarkReviewed} variant="outline" size="sm">
                        Mark {viewModel.violationState.selected.size} as Reviewed
                      </Button>
                      <Button onClick={() => handleExport('selected')} variant="outline" size="sm">
                        Export Selected ({viewModel.violationState.selected.size})
                      </Button>
                    </>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Summary Dashboard */}
          {compileData && compileData.violations.length > 0 && (
            <CompileGateSummary
              overallStatus={compileData.overall_status}
              violations={compileData.violations}
              reviewedCount={viewModel.reviewedCount}
            />
          )}

          {/* Main Content Area */}
          <div className="flex gap-6 mt-6 w-full" style={{ width: '100%', maxWidth: 'none', boxSizing: 'border-box' }}>
            {/* Main Table Area */}
            <div 
              className="flex-1 min-w-0"
              style={{ flex: '1 1 auto', minWidth: 0, width: '100%', maxWidth: 'none', boxSizing: 'border-box' }}
            >
              {/* Filters */}
              <CompileGateFilters
                searchQuery={viewModel.viewState.searchQuery}
                onSearchChange={(q) => viewModel.setViewState({ ...viewModel.viewState, searchQuery: q })}
                selectedSeverities={viewModel.viewState.selectedSeverities}
                onSeverityToggle={handleSeverityToggle}
                selectedRules={viewModel.viewState.selectedRules}
                onRuleToggle={handleRuleToggle}
                groupBy={viewModel.viewState.groupBy}
                onGroupByChange={(g) => viewModel.setViewState({ ...viewModel.viewState, groupBy: g })}
                showReviewed={viewModel.viewState.showReviewed}
                onShowReviewedToggle={() => viewModel.setViewState({ ...viewModel.viewState, showReviewed: !viewModel.viewState.showReviewed })}
                compactMode={viewModel.viewState.compactMode}
                onCompactModeToggle={() => viewModel.setViewState({ ...viewModel.viewState, compactMode: !viewModel.viewState.compactMode })}
                reviewMode={viewModel.viewState.reviewMode}
                onReviewModeToggle={() => viewModel.setViewState({ ...viewModel.viewState, reviewMode: !viewModel.viewState.reviewMode })}
                uniqueSeverities={viewModel.uniqueSeverities}
                uniqueRules={viewModel.uniqueRules}
                hasWBS={hasWBS}
              />

              {/* Table View */}
              <div className="mt-4">
                {tableViolations.length === 0 ? (
                  <div className="p-8 text-center text-nb-text-muted">
                    No violations match your filters
                  </div>
                ) : (
                  <CompileGateTable
                    violations={tableViolations}
                    groupedViolations={viewModel.groupedViolations}
                    groupBy={viewModel.viewState.groupBy}
                    sortColumn={viewModel.viewState.sortColumn}
                    sortDirection={viewModel.viewState.sortDirection}
                    onSort={handleSort}
                    reviewed={viewModel.violationState.reviewed}
                    selected={viewModel.violationState.selected}
                    accepted={viewModel.violationState.accepted}
                    onToggleReviewed={viewModel.toggleReviewed}
                    onToggleSelected={viewModel.toggleSelected}
                    onAcceptRisk={viewModel.acceptRisk}
                    getViolationId={viewModel.getViolationId}
                    compactMode={viewModel.viewState.compactMode}
                    onBulkMarkReviewed={viewModel.bulkMarkReviewed}
                    onSelectAll={viewModel.selectAll}
                    onClearSelection={viewModel.clearSelection}
                    allSelected={allSelected}
                    onExportGroup={(groupKey) => handleExport('group', groupKey)}
                    reviewMode={viewModel.viewState.reviewMode}
                    setSelected={viewModel.setSelected}
                  />
                )}
              </div>
            </div>

            {/* Triage Queue Sidebar */}
            {showTriageQueue && compileData && compileData.violations.length > 0 && (
              <div className="w-80 flex-shrink-0" style={{ maxWidth: 'none' }}>
                <TriageQueue
                  violations={compileData.violations}
                  reviewed={viewModel.violationState.reviewed}
                  getViolationId={viewModel.getViolationId}
                  onViolationClick={handleViolationClick}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default CompileGate;
